import React, { useContext, useState, useEffect, useCallback } from 'react';
import { ShopContext } from 'Context/ShopContext';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import styles from './styles.module.css';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { ROUTES, SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { UserContext } from 'Context/UserContext';
import { Link } from 'react-router-dom';
import { Button, Tooltip, Grid } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import SyncIcon from '@mui/icons-material/Sync';
import Info from 'components/utils/Alert/Info';
import DataTable from 'components/utils/DataTable';
import Grayout from 'components/utils/GrayOut/Grayout';
import { AlertMessagesContext } from 'react-alert-messages';
import ItemService from 'services/ItemService';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ProductionDetailsDialog from 'components/popups/ProductionDetailsDialog';
import { useTranslation } from 'react-i18next';

function ProductionHistory() {
  const { t: tCommon } = useTranslation('common');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [productions, setProductions] = useState([]);
  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [productionDetails, setProductionDetails] = useState(false);

  const subscriptionType = shop?.subscription?.type;
  const userHasStockWritePermission = user?.shop_permissions?.stock_permission === USER_PERMISSIONS.WRITE;

  const handleProductionDetailsView = (production) => {
    setProductionDetails(production);
  };

  const headerData = [
    {
      label: tCommon('Items'),
      id: 'items',
      type: 'callback',
      viewRender: (item) => item?.items?.map((i) => i.name).join(', ') || '-',
    },
    {
      label: tCommon('Created By'),
      id: 'created_by',
      type: 'callback',
      viewRender: (item) => item?.created_by?.username ?? '-',
    },
    {
      label: tCommon('Date'),
      id: 'created_at',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon('View Details')} onClick={() => handleProductionDetailsView(item)}>
              <RemoveRedEyeIcon className={styles.iconButton} />
            </Tooltip>
          </Grid>
        );
      },
    },
  ];

  const _getProductionsFiltered = (productions, searchKey) => {
    if (!searchKey) {
      return productions;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (productions || []).filter((production) =>
      (production.created_by?.username ?? '').toLowerCase().includes(searchKeySmallCase)
    );
  };

  const _getLoadPurchaseRequestParams = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('PRODUCTION_HISTORY');
    return {
      date_start: DateTimeHelpers.getDateYMD(dynamicStartDate),
      date_end: DateTimeHelpers.getDateYMD(dynamicEndDate),
    };
  }, []);

  const getProductions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = _getLoadPurchaseRequestParams();
      const res = await ItemService.getProductions(params);
      setProductions(res);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setIsLoading(false);
  }, [_getLoadPurchaseRequestParams, postAlertMessage]);

  const generateProductionHistory = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('PRODUCTION_HISTORY');

    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);

    getProductions();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
    });
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    generateProductionHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const productionsToDisplay = _getProductionsFiltered(productions, searchText);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Production')} <span className={styles.menuTitle}>{tCommon('History')}</span>
        </h2>
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tCommon('To get production history options upgrade subscription to premium.')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tCommon('Production History from {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                    to: DateTimeHelpers.convertDateToDMY(filterToDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                {userHasStockWritePermission && (
                  <Link to={ROUTES.NEW_PRODUCTION} style={{ textDecorationColor: 'transparent' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      style={{ backgroundColor: '#ff851b' }}
                      className={styles.actionBtn}
                    >
                      <AddShoppingCartIcon className={styles.actionBtnIcon} />
                      {tCommon('New Production')}
                    </Button>
                  </Link>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search production')}
                  />
                  <Tooltip title={tCommon('Search')} placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="PRODUCTION_HISTORY"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Load Production')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateProductionHistory}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tCommon('Set filters and click load button')}
              content={tCommon('Set filters and click generate button')}
            />
          ) : (
            (productions || isLoading) &&
            ((productionsToDisplay && productionsToDisplay.length) || isLoading ? (
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable columns={headerData} rows={productionsToDisplay || []} isLoading={isLoading} />
              </div>
            ) : (
              !isLoading && (
                <Info
                  title={tCommon('No production to list')}
                  content={tCommon("You haven't any production to list with given properties")}
                />
              )
            ))
          )}
        </>
      )}
      {productionDetails && (
        <ProductionDetailsDialog
          production={productionDetails}
          handleClose={() => {
            setProductionDetails(null);
          }}
        />
      )}
    </div>
  );
}

export default withConsoleBase(ProductionHistory);
